import { createTheme } from '@mui/material/styles';

// Custom theme matching GMB Fitness PWA brand colors
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#16C0E8', // GMB cyan/turquoise (matches PWA lightBlue)
      dark: '#0EA0C3', // Darker cyan (matches PWA blue)
    },
    secondary: {
      main: '#d84181', // GMB pink/magenta (matches PWA ruby)
      dark: '#AC4887', // Darker pink (matches PWA fandango)
    },
    background: {
      default: '#F0F2F5', // Light gray background (matches PWA)
      paper: '#FFFFFF', // White background for cards
    },
    text: {
      primary: '#21262D', // Dark text (matches PWA black)
      secondary: '#6C7888', // Grey text (matches PWA grey)
    },
    divider: '#F0F2F5', // Subtle dividers
    grey: {
      100: '#F8F9FA',
      200: '#F0F2F5',
      300: '#DADEE3',
      400: '#B6BEC9',
      500: '#6C7888',
    },
  },
  typography: {
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontSize: 14,
    h1: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.3,
      color: '#21262D',
    },
    h2: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.3,
      color: '#21262D',
    },
    h3: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
      color: '#21262D',
    },
    h4: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.4,
      color: '#21262D',
    },
    h5: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.5,
      color: '#21262D',
    },
    h6: {
      fontSize: '0.875rem',
      fontWeight: 600,
      lineHeight: 1.5,
      color: '#21262D',
    },
    body1: {
      fontSize: '0.9375rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          boxShadow: '0 0 26px 0 rgba(33, 38, 45, 0.1)',
          color: '#21262D',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#FFFFFF',
          width: 72,
          borderRight: '1px solid #F0F2F5',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 20px',
          fontSize: '0.9375rem',
          fontWeight: 500,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(22, 192, 232, 0.25)',
          },
        },
        containedPrimary: {
          backgroundColor: '#16C0E8',
          '&:hover': {
            backgroundColor: '#0EA0C3',
          },
        },
        containedSecondary: {
          backgroundColor: '#d84181',
          '&:hover': {
            backgroundColor: '#AC4887',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
        },
        elevation1: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '0.9375rem',
          padding: '16px',
        },
        head: {
          fontWeight: 600,
          color: '#21262D',
          backgroundColor: '#F8F9FA',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontSize: '0.8125rem',
        },
      },
    },
    RaLayout: {
      styleOverrides: {
        root: {
          '& .RaLayout-content': {
            padding: '24px',
          },
        },
        content: {
          flex: '1 1 auto',
          padding: '24px',
        },
        contentWithSidebar: {
          display: 'flex',
          flexDirection: 'row',
        },
      },
    },
    RaList: {
      styleOverrides: {
        root: {
          '& .RaList-main': {
            boxShadow: 'none',
          },
        },
      },
    },
    RaDatagrid: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          '& .RaDatagrid-headerCell': {
            fontWeight: 600,
          },
        },
      },
    },
    RaMenuItemLink: {
      styleOverrides: {
        root: {
          fontSize: '0.9375rem',
          padding: '12px',
          margin: '4px 8px',
          borderRadius: 8,
          justifyContent: 'center',
          minWidth: '56px',
          '&.RaMenuItemLink-active': {
            backgroundColor: '#E8F8FC',
            color: '#0EA0C3',
            fontWeight: 500,
          },
          '&:hover': {
            backgroundColor: '#F8F9FA',
          },
          '& .MuiListItemIcon-root': {
            minWidth: 'auto',
            marginRight: 0,
          },
        },
      },
    },
    // Make Edit/Create views full width
    RaEdit: {
      styleOverrides: {
        root: {
          '& .RaEdit-main': {
            maxWidth: '100%',
          },
          '& .RaEdit-card': {
            maxWidth: '100%',
          },
        },
      },
    },
    RaCreate: {
      styleOverrides: {
        root: {
          '& .RaCreate-main': {
            maxWidth: '100%',
          },
          '& .RaCreate-card': {
            maxWidth: '100%',
          },
        },
      },
    },
    // Make SimpleForm inputs full width
    RaSimpleForm: {
      styleOverrides: {
        root: {
          '& .MuiCardContent-root': {
            maxWidth: '100%',
          },
          '& .ra-input': {
            width: '100%',
          },
          '& .MuiFormControl-root': {
            width: '100%',
          },
        },
      },
    },
    // Make TabbedForm inputs full width
    RaTabbedForm: {
      styleOverrides: {
        root: {
          '& .ra-input': {
            width: '100%',
          },
          '& .MuiFormControl-root': {
            width: '100%',
          },
        },
      },
    },
    // Make SimpleShowLayout full width
    RaSimpleShowLayout: {
      styleOverrides: {
        root: {
          width: '100%',
          '& .ra-input': {
            width: '100%',
          },
          '& .MuiFormControl-root': {
            width: '100%',
          },
        },
      },
    },
  },
});

export default theme;
